import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

function SalesHistory() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token")?.trim();

  const authHeaders = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
    [token]
  );

  const [sales, setSales] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [paymentMethod, setPaymentMethod] = useState("all");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const loadSales = async () => {
    try {
      setIsLoading(true);
      setMessage("");

      const params = new URLSearchParams();

      if (search.trim()) params.append("search", search.trim());
      if (status !== "all") params.append("status", status);
      if (paymentMethod !== "all") params.append("paymentMethod", paymentMethod);

      const res = await axios.get(`${API_URL}/sales?${params.toString()}`, authHeaders);

      setSales(res.data || []);
    } catch (error) {
      setMessage(error.response?.data?.message || "Could not load sales history.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadSales();
    }
  }, [token, status, paymentMethod]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadSales();
  };

  const voidSale = async (sale) => {
    const reason = window.prompt(
      `Enter reason for voiding receipt ${sale.receiptNumber}:`
    );

    if (!reason) return;

    const confirmed = window.confirm(
      `Are you sure you want to void ${sale.receiptNumber}? Stock will be restored.`
    );

    if (!confirmed) return;

    try {
      setMessage("");

      await axios.put(
        `${API_URL}/sales/${sale._id}/void`,
        { reason },
        authHeaders
      );

      setMessage("Sale voided successfully. Stock has been restored.");
      loadSales();
    } catch (error) {
      setMessage(error.response?.data?.message || "Could not void sale.");
    }
  };

  const formatDate = (dateValue) => {
    return new Date(dateValue).toLocaleString("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Sales History</h1>
          <p style={styles.subtitle}>
            Search receipts, view cashier/terminal activity, reprint receipts, and void sales.
          </p>
        </div>
      </div>

      {message && <p style={styles.message}>{message}</p>}

      <form onSubmit={handleSearchSubmit} style={styles.filters}>
        <input
          style={styles.input}
          placeholder="Search receipt, cashier, terminal, or product"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          style={styles.select}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="completed">Completed</option>
          <option value="voided">Voided</option>
        </select>

        <select
          style={styles.select}
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
        >
          <option value="all">All Payments</option>
          <option value="cash">Cash</option>
          <option value="card">Card</option>
        </select>

        <button type="submit" style={styles.primaryButton}>
          Search
        </button>
      </form>

      <div style={styles.card}>
        {isLoading ? (
          <p>Loading sales...</p>
        ) : sales.length === 0 ? (
          <p>No sales found.</p>
        ) : (
          <div style={styles.salesList}>
            {sales.map((sale) => (
              <div key={sale._id} style={styles.saleRow}>
                <div style={styles.saleTop}>
                  <div>
                    <strong>{sale.receiptNumber}</strong>
                    <p style={styles.smallText}>{formatDate(sale.createdAt)}</p>
                  </div>

                  <div style={styles.amountBox}>
                    <strong>£{Number(sale.totalAmount).toFixed(2)}</strong>
                    <span
                      style={{
                        ...styles.status,
                        background:
                          sale.status === "voided" ? "#fee2e2" : "#dcfce7",
                        color: sale.status === "voided" ? "#991b1b" : "#166534",
                      }}
                    >
                      {sale.status}
                    </span>
                  </div>
                </div>

                <div style={styles.infoGrid}>
                  <p>
                    <strong>Cashier:</strong> {sale.cashier?.name || "Unknown"}
                  </p>
                  <p>
                    <strong>Terminal:</strong>{" "}
                    {sale.terminal?.terminalName || "Unknown Terminal"}
                  </p>
                  <p>
                    <strong>Payment:</strong> {sale.paymentMethod}
                  </p>
                  <p>
                    <strong>Refund:</strong> {sale.refundStatus || "none"}
                  </p>
                </div>

                <div style={styles.itemsBox}>
                  {sale.items?.map((item, index) => (
                    <div key={index} style={styles.itemLine}>
                      <span>
                        {item.name} × {item.quantity} {item.unitLabel}
                      </span>
                      <strong>£{Number(item.lineTotal).toFixed(2)}</strong>
                    </div>
                  ))}
                </div>

                {sale.status === "voided" && (
                  <p style={styles.voidText}>
                    Voided by {sale.voidedBy?.name || "Unknown"} —{" "}
                    {sale.voidReason || "No reason provided"}
                  </p>
                )}

                <div style={styles.actions}>
                  <button
                    type="button"
                    onClick={() => navigate(`/receipt/${sale._id}?autoPrint=true`)}
                    style={styles.secondaryButton}
                  >
                    Reprint Receipt
                  </button>

                  {sale.status !== "voided" && (
                    <button
                      type="button"
                      onClick={() => voidSale(sale)}
                      style={styles.dangerButton}
                    >
                      Void Sale
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: "24px",
    fontFamily: "Arial, sans-serif",
    background: "#f4f6f8",
    minHeight: "100vh",
  },

  header: {
    marginBottom: "20px",
  },

  title: {
    margin: 0,
  },

  subtitle: {
    margin: "6px 0 0",
    color: "#555",
  },

  message: {
    padding: "12px",
    background: "#fff3cd",
    borderRadius: "8px",
    marginBottom: "16px",
  },

  filters: {
    display: "grid",
    gridTemplateColumns: "1fr 180px 180px 130px",
    gap: "12px",
    marginBottom: "18px",
  },

  input: {
    padding: "14px",
    fontSize: "16px",
    borderRadius: "8px",
    border: "1px solid #ccc",
  },

  select: {
    padding: "14px",
    fontSize: "16px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    background: "white",
  },

  primaryButton: {
    padding: "14px",
    fontSize: "16px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    fontWeight: "bold",
  },

  card: {
    background: "white",
    padding: "20px",
    borderRadius: "14px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },

  salesList: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  saleRow: {
    border: "1px solid #eee",
    borderRadius: "12px",
    padding: "16px",
  },

  saleTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
  },

  amountBox: {
    textAlign: "right",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    alignItems: "flex-end",
  },

  smallText: {
    margin: "4px 0 0",
    color: "#555",
  },

  status: {
    display: "inline-block",
    padding: "5px 10px",
    borderRadius: "999px",
    fontSize: "13px",
    fontWeight: "bold",
    textTransform: "capitalize",
  },

  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "10px",
    marginTop: "14px",
    color: "#333",
  },

  itemsBox: {
    marginTop: "12px",
    background: "#f9fafb",
    borderRadius: "10px",
    padding: "12px",
  },

  itemLine: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    borderBottom: "1px solid #eee",
  },

  voidText: {
    marginTop: "12px",
    color: "#991b1b",
    fontWeight: "bold",
  },

  actions: {
    display: "flex",
    gap: "10px",
    marginTop: "14px",
  },

  secondaryButton: {
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    background: "white",
    cursor: "pointer",
  },

  dangerButton: {
    padding: "10px 12px",
    borderRadius: "8px",
    border: "none",
    background: "#fee2e2",
    color: "#991b1b",
    cursor: "pointer",
    fontWeight: "bold",
  },
};

export default SalesHistory;